import React, { useState } from 'react';
import {
  Brain,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Zap,
  Target,
  TrendingUp,
  TrendingDown,
  Shield,
  ChevronDown,
  ChevronUp,
  Loader2,
  XCircle,
  ArrowRight,
  Info
} from 'lucide-react';
import { Lead } from '../types';
import { leadIntelligenceService } from '../services/leadIntelligenceService';

interface LeadIntelligencePanelProps {
  lead: Lead;
  onLeadUpdated?: () => void;
}

const PROJECT_LINE_LABELS: Record<string, { label: string; description: string }> = {
  A_CORE_SYSTEM: {
    label: 'A — Core System',
    description: 'CRM, ERP o plataforma de gestión interna'
  },
  B_BIG_PLATFORM: {
    label: 'B — Big Platform',
    description: 'Marketplace, SaaS o plataforma digital compleja'
  },
  C_RESCUE_IMPROVEMENTS: {
    label: 'C — Rescue & Improvements',
    description: 'Rescate, mejora o migración de proyecto existente'
  }
};

const ACTION_LABELS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  propose_phase0: {
    label: 'Proponer Fase 0 (Descubrimiento)',
    color: 'text-green-800',
    bg: 'bg-green-50',
    border: 'border-green-200'
  },
  propose_bounded_phase: {
    label: 'Proponer Fase Acotada (MVP / PoC)',
    color: 'text-blue-800',
    bg: 'bg-blue-50',
    border: 'border-blue-200'
  },
  request_more_information: {
    label: 'Solicitar Más Información',
    color: 'text-amber-800',
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  },
  reject_politely: {
    label: 'Descartar con Educación',
    color: 'text-red-800',
    bg: 'bg-red-50',
    border: 'border-red-200'
  }
};

const TIER_CONFIG: Record<string, { label: string; color: string; bg: string; ring: string }> = {
  HOT: { label: 'HOT', color: 'text-red-700', bg: 'bg-red-100', ring: 'ring-red-300' },
  WARM: { label: 'WARM', color: 'text-amber-700', bg: 'bg-amber-100', ring: 'ring-amber-300' },
  COLD: { label: 'COLD', color: 'text-slate-600', bg: 'bg-slate-100', ring: 'ring-slate-300' }
};

const RISK_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  LOW: { label: 'LOW RISK', color: 'text-green-800', bg: 'bg-green-50', dot: 'bg-green-500' },
  MEDIUM: { label: 'MEDIUM RISK', color: 'text-amber-800', bg: 'bg-amber-50', dot: 'bg-amber-500' },
  HIGH: { label: 'HIGH RISK', color: 'text-red-800', bg: 'bg-red-50', dot: 'bg-red-500' }
};

const CONFIDENCE_CONFIG: Record<string, { label: string; color: string }> = {
  HIGH: { label: 'Alta confianza', color: 'text-green-700' },
  MEDIUM: { label: 'Confianza media', color: 'text-amber-700' },
  LOW: { label: 'Baja confianza', color: 'text-slate-500' }
};

const CATEGORY_LABELS: Record<string, string> = {
  business_problem: 'Problema de negocio',
  idea_only: 'Solo idea',
  spam: 'Spam',
  unclear: 'Poco claro'
};

function ScoreBar({ value, max = 25 }: { value: number; max?: number }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const color = pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function ProbabilityBar({ value, riskLevel }: { value: number; riskLevel?: string }) {
  const color =
    riskLevel === 'LOW' ? 'bg-green-500' :
    riskLevel === 'MEDIUM' ? 'bg-amber-400' :
    riskLevel === 'HIGH' ? 'bg-red-500' :
    value >= 65 ? 'bg-green-500' : value >= 35 ? 'bg-amber-400' : 'bg-red-500';

  return (
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

const LeadIntelligencePanel: React.FC<LeadIntelligencePanelProps> = ({ lead, onLeadUpdated }) => {
  const [rerunning, setRerunning] = useState(false);
  const [stage1Open, setStage1Open] = useState(false);

  const handleRerun = async () => {
    setRerunning(true);
    try {
      await leadIntelligenceService.rerunAnalysis(lead.id);
      setTimeout(() => {
        setRerunning(false);
        onLeadUpdated?.();
      }, 1500);
    } catch {
      setRerunning(false);
    }
  };

  if (lead.analysisStatus === 'pending' || !lead.analysisStatus) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Brain className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Lead Intelligence v2</h3>
        </div>
        <div className="flex flex-col items-center py-8 space-y-3">
          <div className="flex items-center space-x-3 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            <span className="text-sm">Analizando lead en segundo plano...</span>
          </div>
          <div className="w-48 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-400 rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
          <p className="text-xs text-gray-400">Recarga la página en unos segundos para ver los resultados</p>
        </div>
      </div>
    );
  }

  if (lead.analysisStatus === 'failed') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Brain className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Lead Intelligence v2</h3>
        </div>
        <div className="flex flex-col items-center py-6 space-y-4">
          <div className="p-3 bg-red-50 rounded-full">
            <XCircle className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-sm text-gray-600 text-center">El análisis no pudo completarse.</p>
          <button
            onClick={handleRerun}
            disabled={rerunning}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {rerunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span>Reintentar análisis</span>
          </button>
        </div>
      </div>
    );
  }

  const isRejected = lead.analysisStage1Result === 'reject';

  if (isRejected) {
    const categoryLabel = lead.analysisStage1Category
      ? CATEGORY_LABELS[lead.analysisStage1Category] || lead.analysisStage1Category
      : 'Desconocido';

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Brain className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Lead Intelligence v2</h3>
              {lead.analysisVersion && (
                <span className="text-xs text-gray-400">{lead.analysisVersion}</span>
              )}
            </div>
          </div>
          <button
            onClick={handleRerun}
            disabled={rerunning}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Relanzar análisis"
          >
            {rerunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>

        <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Filtrado en Stage 1 — <span className="text-gray-900">{categoryLabel}</span>
            </p>
            {lead.analysisStage1Reason && (
              <p className="text-sm text-gray-500">{lead.analysisStage1Reason}</p>
            )}
          </div>
        </div>

        {lead.aiAnalyzedAt && (
          <p className="text-xs text-gray-400 mt-3">
            Analizado: {lead.aiAnalyzedAt.toLocaleString()}
          </p>
        )}
      </div>
    );
  }

  const tier = lead.leadTier ? TIER_CONFIG[lead.leadTier] : null;
  const action = lead.recommendedAction ? ACTION_LABELS[lead.recommendedAction] : null;
  const projectLine = lead.projectLine ? PROJECT_LINE_LABELS[lead.projectLine] : null;
  const breakdown = lead.leadScoreBreakdown;
  const hasProbability = lead.closeProbability != null && lead.proposalProbability != null;
  const riskConfig = lead.riskLevel ? RISK_CONFIG[lead.riskLevel] : null;
  const confidenceConfig = lead.confidenceLevel ? CONFIDENCE_CONFIG[lead.confidenceLevel] : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Brain className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Lead Intelligence v2</h3>
            {lead.analysisVersion && (
              <span className="text-xs text-gray-400">{lead.analysisVersion}</span>
            )}
          </div>
        </div>
        <button
          onClick={handleRerun}
          disabled={rerunning}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          title="Relanzar análisis"
        >
          {rerunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      <div className="p-6 space-y-5">

        {/* 1. Tier + Score */}
        {tier && (
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ring-2 ${tier.bg} ${tier.ring}`}>
              <Zap className={`w-4 h-4 ${tier.color}`} />
              <span className={`text-lg font-bold tracking-wide ${tier.color}`}>{tier.label}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-gray-900">{lead.score}</span>
              <span className="text-xs text-gray-400 -mt-1">/ 100</span>
            </div>
          </div>
        )}

        {/* 2. Project Line */}
        {projectLine && (
          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Target className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-800">{projectLine.label}</p>
              <p className="text-xs text-gray-500">{projectLine.description}</p>
            </div>
          </div>
        )}

        {/* 3. Probability Block */}
        {hasProbability && (
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 divide-x divide-gray-100">
              <div className="p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Cierre</p>
                <div className="flex items-baseline space-x-1 mb-2">
                  <span className={`text-3xl font-bold ${
                    lead.riskLevel === 'LOW' ? 'text-green-700' :
                    lead.riskLevel === 'MEDIUM' ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {lead.closeProbability}%
                  </span>
                </div>
                <ProbabilityBar value={lead.closeProbability!} riskLevel={lead.riskLevel} />
              </div>
              <div className="p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Propuesta</p>
                <div className="flex items-baseline space-x-1 mb-2">
                  <span className="text-3xl font-bold text-gray-700">
                    {lead.proposalProbability}%
                  </span>
                </div>
                <ProbabilityBar value={lead.proposalProbability!} />
              </div>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {riskConfig && (
                  <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${riskConfig.bg} ${riskConfig.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${riskConfig.dot}`} />
                    <span>{riskConfig.label}</span>
                  </span>
                )}
                {confidenceConfig && (
                  <span className={`text-xs ${confidenceConfig.color}`}>
                    {confidenceConfig.label}
                  </span>
                )}
              </div>
              {lead.probabilityVersion && (
                <span className="text-xs text-gray-300">{lead.probabilityVersion}</span>
              )}
            </div>
            {lead.probabilitySummary && (
              <div className="px-4 pb-4 pt-2">
                <p className="text-xs text-gray-500 leading-relaxed">{lead.probabilitySummary}</p>
              </div>
            )}
            {lead.riskReasons && lead.riskReasons.length > 0 && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Factores de riesgo</p>
                <ul className="space-y-1">
                  {lead.riskReasons.map((reason, i) => (
                    <li key={i} className="flex items-start space-x-2">
                      <TrendingDown className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* 4. Recommended Action */}
        {action && (
          <div className={`flex items-center space-x-3 p-3 rounded-lg border ${action.bg} ${action.border}`}>
            <ArrowRight className={`w-4 h-4 flex-shrink-0 ${action.color}`} />
            <p className={`text-sm font-semibold ${action.color}`}>{action.label}</p>
          </div>
        )}

        {/* 5. Red Flags */}
        {lead.leadRedFlags && lead.leadRedFlags.length > 0 ? (
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <p className="text-sm font-semibold text-gray-800">Red Flags</p>
            </div>
            <ul className="space-y-1.5">
              {lead.leadRedFlags.map((flag, i) => (
                <li key={i} className="flex items-start space-x-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0 mt-1.5" />
                  <span className="text-sm text-gray-700">{flag}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-green-700">
            <CheckCircle className="w-4 h-4" />
            <p className="text-sm font-medium">Sin red flags detectadas</p>
          </div>
        )}

        {/* 5. Score Breakdown */}
        {breakdown && (
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <TrendingUp className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-semibold text-gray-800">Desglose del Score</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {([
                { key: 'pain', label: 'Dolor' },
                { key: 'fit', label: 'Encaje' },
                { key: 'budget', label: 'Presupuesto' },
                { key: 'seriousness', label: 'Seriedad' }
              ] as const).map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{label}</span>
                    <span className="text-xs font-semibold text-gray-800">{breakdown[key]}<span className="text-gray-400 font-normal">/25</span></span>
                  </div>
                  <ScoreBar value={breakdown[key]} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6. Summary */}
        {lead.leadAnalysisSummary && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Resumen</p>
            <p className="text-sm text-gray-700 leading-relaxed">{lead.leadAnalysisSummary}</p>
          </div>
        )}

        {/* Pain signals / Fit signals compact */}
        {((lead.leadPainSignals && lead.leadPainSignals.length > 0) ||
          (lead.leadFitSignals && lead.leadFitSignals.length > 0)) && (
          <div className="grid grid-cols-2 gap-4 pt-1">
            {lead.leadPainSignals && lead.leadPainSignals.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Señales de dolor</p>
                <ul className="space-y-1">
                  {lead.leadPainSignals.map((s, i) => (
                    <li key={i} className="flex items-start space-x-1.5">
                      <span className="w-1.5 h-1.5 bg-amber-400 rounded-full flex-shrink-0 mt-1.5" />
                      <span className="text-xs text-gray-600">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {lead.leadFitSignals && lead.leadFitSignals.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Señales de encaje</p>
                <ul className="space-y-1">
                  {lead.leadFitSignals.map((s, i) => (
                    <li key={i} className="flex items-start space-x-1.5">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full flex-shrink-0 mt-1.5" />
                      <span className="text-xs text-gray-600">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* 7. Stage 1 info (collapsible) */}
        {lead.analysisStage1Result && (
          <div className="border-t border-gray-100 pt-4">
            <button
              onClick={() => setStage1Open(o => !o)}
              className="flex items-center space-x-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
              <span>Stage 1 — Filtro rápido</span>
              {stage1Open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {stage1Open && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs space-y-1">
                <p><span className="text-gray-500">Resultado:</span> <span className="font-medium text-gray-700">{lead.analysisStage1Result}</span></p>
                {lead.analysisStage1Category && (
                  <p><span className="text-gray-500">Categoría:</span> <span className="font-medium text-gray-700">{CATEGORY_LABELS[lead.analysisStage1Category] || lead.analysisStage1Category}</span></p>
                )}
                {lead.analysisStage1Reason && (
                  <p><span className="text-gray-500">Razón:</span> <span className="text-gray-700">{lead.analysisStage1Reason}</span></p>
                )}
              </div>
            )}
          </div>
        )}

        {lead.aiAnalyzedAt && (
          <p className="text-xs text-gray-400">
            Analizado: {lead.aiAnalyzedAt.toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default LeadIntelligencePanel;
